import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { HelpTooltip } from '../../components/client/HelpTooltip'
import { domainsAPI, serverAPI, slotsAPI } from '@hpanel/web-common/api/client'
import type { Domain, DomainDnsCheck } from '@hpanel/web-common/api/types'

import { useAuth } from '@hpanel/web-common/contexts/AuthContext'

/**
 * DomainManagement page component.
 *
 * Displays all user domains with resource usage per domain.
 * Features:
 * - List all domains with usage stats
 * - Request new sites (an administrator approves them)
 * - Manage/delete existing domains
 */

export const DomainManagement: React.FC = () => {
  const { user } = useAuth()
  const [domains, setDomains] = useState<Domain[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Ids of the domains that are a site's primary. Only an administrator can
  // remove one, so they get no Delete button. Null until known (no button yet);
  // if the list cannot be loaded the buttons show and the server still refuses
  // a primary with its own detail.
  const [primaryIds, setPrimaryIds] = useState<Set<number> | null>(null)

  // Per-domain DNS signal, keyed by domain id. A failed check leaves the
  // entry absent (no badge) and is logged.
  const [dnsChecks, setDnsChecks] = useState<Record<number, DomainDnsCheck>>({})

  const checkDns = (list: Domain[]) => {
    for (const d of list) {
      serverAPI
        .dnsCheck(d.id)
        .then((check) => setDnsChecks((prev) => ({ ...prev, [d.id]: check })))
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error(`[DomainManagement] DNS check failed for ${d.name}`, e)
        })
    }
  }

  const dnsBadge = (check: DomainDnsCheck): { text: string; className: string } | null => {
    switch (check.status) {
      case 'ok':
        return { text: 'DNS OK', className: 'text-green-700 dark:text-green-400' }
      case 'elsewhere': {
        const target = check.expected.join(', ')
        return {
          text: `DNS points elsewhere (${check.resolved.join(', ')}) \u2014 update your A record to ${target}`,
          className: 'text-amber-700 dark:text-amber-400',
        }
      }
      case 'unresolved':
        return { text: 'DNS not set up yet', className: 'text-gray-600 dark:text-gray-400' }
      default:
        return null
    }
  }

  const fetchDomains = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await domainsAPI.list(user?.id)
      setDomains(response.domains)
      checkDns(response.domains)
      slotsAPI
        .list()
        .then((r) => setPrimaryIds(new Set(r.slots.map((slot) => slot.primary.id))))
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error('[DomainManagement] could not load which domains are site primaries', e)
          setPrimaryIds(new Set())
        })
    } catch (err) {
      setError('Failed to load domains. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  /**
   * Delete a domain (and its backing container) after explicit confirmation.
   * Matches the pattern used in pages/host/Packages.tsx.
   */
  const handleDelete = async (id: number, name: string) => {
    const confirmed = window.confirm(
      `Delete ${name}? This permanently deletes its site files, mailboxes, databases and subdomains (including staging copies), and destroys its container. This cannot be undone.`,
    )
    if (!confirmed) return
    try {
      setError(null)
      await domainsAPI.delete(id, { force: true })
      await fetchDomains()
    } catch (err) {
      setError(
        err instanceof Error
          ? `Failed to delete ${name}: ${err.message}`
          : `Failed to delete ${name}. Please try again.`,
      )
    }
  }

  useEffect(() => {
    fetchDomains()
  }, [])

  /**
   * Get status badge color based on domain status.
   */
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'suspended':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  /**
   * Render a usage progress bar.
   */
  const UsageBar: React.FC<{ used: number; max: number; label: string }> = ({
    used,
    max,
    label,
  }) => {
    const percentage = max > 0 ? Math.min((used / max) * 100, 100) : 0
    const barColor =
      percentage > 80
        ? 'bg-red-500'
        : percentage > 60
          ? 'bg-yellow-500'
          : 'bg-blue-500'

    return (
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
          <span>{label}</span>
          <span>
            {used} / {max} MB
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className={`${barColor} h-2 rounded-full transition-all`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    )
  }

  return (
    <>
      <div data-testid="domain-management-page" className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 data-testid="page-title" className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
              Domain Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Manage your domains and monitor resource usage
            </p>
          </div>

          {/* Request a site button */}
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <Link
              to="/client/sites/request"
              data-testid="request-site-button"
              className="btn-primary"
            >
              + Request a site
            </Link>
            <HelpTooltip text="Ask for a new site on a domain you own. An administrator reviews the request; to put another domain on a site you already have, use Park a domain on the Sites page" />
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div data-testid="domain-management-error" className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Loading domains...
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && domains.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              No domains found. Request your first site to get started.
            </p>
          </div>
        )}

        {/* Domain cards */}
        {!loading && domains.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {domains.map((domain) => (
              <div
                key={domain.id}
                data-testid={`domain-card-${domain.name.toLowerCase()}`}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-4"
              >
                {/* Domain name and status */}
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {domain.name}
                  </h3>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(domain.status)}`}
                  >
                    {domain.status}
                  </span>
                </div>

                {dnsChecks[domain.id] && dnsBadge(dnsChecks[domain.id]) && (
                  <p
                    data-testid={`domain-dns-status-${domain.name.toLowerCase()}`}
                    className={`text-xs ${dnsBadge(dnsChecks[domain.id])!.className}`}
                  >
                    {dnsBadge(dnsChecks[domain.id])!.text}
                  </p>
                )}

                {/* Resource usage bars */}
                <div className="space-y-3">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-600 dark:text-gray-400 flex-1">Resources</span>
                    <HelpTooltip text="Disk and bandwidth usage for this domain" position="left" />
                  </div>
                  <UsageBar
                    used={Math.round((domain.disk_usage_bytes ?? 0) / 1048576)}
                    max={1024}
                    label="Disk Usage"
                  />
                  <UsageBar
                    used={Math.round((domain.bandwidth_usage_bytes ?? 0) / 1048576)}
                    max={10240}
                    label="Bandwidth"
                  />
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <Link
                    to={`/client/domains/${domain.id}`}
                    data-testid={`domain-card-manage-${domain.name.toLowerCase()}`}
                    aria-label={`Manage ${domain.name}`}
                    className="btn-secondary text-sm flex-1 text-center"
                  >
                    Manage
                  </Link>
                  {primaryIds !== null && !primaryIds.has(domain.id) && (
                    <button
                      type="button"
                      data-testid="domain-card-delete"
                      onClick={() => handleDelete(domain.id, domain.name)}
                      className="text-sm px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                      aria-label={`Delete ${domain.name}`}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
